import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Tracks a single slide that has been temporarily moved elsewhere in the DOM (leaving a
 * placeholder in its original spot) so it can be restored later. This is the one primitive both
 * the reactive "jump/wrap to a non-adjacent slide" transition AND the proactive "park the
 * opposite-end slide next to the current boundary so native scroll has somewhere to go" trick are
 * built on - previously each had its own independent copy of this move/track/restore logic.
 */
class SlideRelocation {
  /** @type {HTMLElement} */
  slide;
  /** @type {HTMLElement} */
  #placeholder;

  /**
   * @param {HTMLElement} slide - The slide to relocate.
   * @param {HTMLElement} anchor - The slide to relocate it next to.
   * @param {'before' | 'after'} position - Which side of `anchor` to place it on.
   */
  constructor(slide, anchor, position) {
    this.slide = slide;
    this.#placeholder = document.createElement('slideshow-slide');
    slide.before(this.#placeholder);
    anchor[position](slide);
  }

  /** Moves `slide` back to its original (placeholder's) DOM position. */
  restore() {
    this.#placeholder.replaceWith(this.slide);
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    // Count all slides in the DOM, not just currently-visible ones (`this.slides` filters out
    // `[hidden]` slides). Product cards render every variant's image up front but hide all but
    // the first one for performance (see card-gallery.liquid) and reveal one on demand via
    // `select({ id })` when a swatch is chosen - that reveal path requires `#scroll` to exist,
    // so a slideshow with multiple hidden slides still needs the full setup below, not the
    // no-controls path, even though only one slide is visible at connect time.
    const slideCount = this.refs.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    // Leave the DOM in logical order in case this element is reconnected (e.g. collection
    // filtering morphs slideshow galleries in place, see attributeChangedCallback above). Covers
    // both the proactive boundary-prime relocation AND a reactive reorder transition that may
    // still be mid-flight (awaiting its scroll settle) at disconnect time - that coroutine checks
    // `isConnected` after each await and will no-op on wake, but won't undo its own relocation, so
    // restore it here rather than leaving the slide parked.
    this.#restoreBoundaryPrime();
    this.#activeTransition?.relocation?.restore();

    // Discard any queued request - nothing left to replay it against once reconnected (a fresh
    // navigation would use current state, not a stale request from before this teardown).
    this.#queuedRequest = null;
    this.#activeTransition = null;

    // Same reasoning for the clone-loop landing state: nothing left to resolve/replay against
    // once reconnected, and any pointers tracked in `#pointersDown` are no longer this element's
    // to track (a fresh `#setupSlideshow()` on reconnect starts these clean). Also clear
    // `#cloneLandingInFlight` itself (matching `#activeTransition` above) - without this, a
    // disconnect+synchronous-reconnect mid-correction could leave a stale in-flight coroutine
    // free to act on a freshly-rebuilt `#scroll` once `isConnected` reads `true` again.
    this.#cloneLandingInFlight = false;
    this.#queuedCloneRequest = null;
    this.#restingOnClone = null;
    this.#pointersDown.clear();

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      if (this.cloneLoop) {
        // Matches #setupSlideshow()'s listener target - see that call site's comment for why
        // these are on `this`, not `scroller`.
        this.removeEventListener('pointerdown', this.#handlePointerDown);
        this.removeEventListener('pointerup', this.#handlePointerLift);
        this.removeEventListener('pointercancel', this.#handlePointerLift);
        this.removeEventListener('pointercapturelost', this.#handlePointerLift);
      }
      this.#scroll.destroy();
      // Null out the reference alongside destroying it - otherwise it stays truthy-but-stale
      // (pointing at a dead Scroller) for the whole window between disconnect and the next
      // #setupSlideshow() reassigning it, which every `if (!this.#scroll) return;` guard
      // elsewhere in this class relies on being falsy to correctly no-op while scroll state is
      // genuinely unavailable.
      this.#scroll = undefined;
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    this.#removeBoundaryClones();

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}|'next'|'previous'} input - The index or id of the slide to
   *   select, or the symbolic direction 'next'/'previous' (resolved against `current` at the time
   *   this request actually runs, not when it's made - see `next()`/`previous()`).
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (!this.refs.slides?.length || !this.#scroll) return;

    event?.preventDefault();

    if (this.cloneLoop) {
      if (this.#cloneLandingInFlight) {
        this.#queuedCloneRequest = { kind: 'select', input, event, options };
        return;
      }
      this.#performCloneLoopSelect(input, event, options);
      return;
    }

    // Resolve the requested index against CURRENT logical state. If a transition is already in
    // flight this is intentionally resolved again once it's this request's turn to run (see
    // `#drainQueue()`), not resolved now and replayed stale - `current`/slide identity may have
    // moved on by then.
    this.#enqueueOrRun({ kind: 'select', input, event, options });
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    // Pass the symbolic 'next' direction rather than eagerly resolving `this.nextIndex` here:
    // `nextIndex` is computed from `current` NOW, but if a transition is already in flight this
    // call gets queued and only actually runs once that transition settles - by then `current` may
    // have moved on (e.g. rapid arrow-key presses, or a next()/previous() queued behind a drag),
    // and replaying a frozen absolute index computed against stale `current` targets the wrong
    // slide. #performSelect resolves 'next'/'previous' to an index against CURRENT `current` at the
    // moment it actually runs, matching how it already re-resolves everything else. (This was a
    // real gap in the queueing redesign - confirmed by tracing the exact rapid-arrow-key sequence
    // that had already been live-tested as broken.)
    this.select('next', event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    // See the matching comment in next() above - same reasoning.
    this.select('previous', event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Whether the user's gesture on this slideshow is still actively in progress - true while
   * EITHER a pointer is physically down (`#pointersDown`, native touch/scroll-snap swiping,
   * tracked for `cloneLoop` slideshows only; or `#dragging`, the desktop mouse-drag-to-scroll
   * gesture, tracked for all slideshows - two structurally different gesture sources, see
   * `#handleMouseDown`'s doc) OR the scroll itself is still actively moving
   * (`Scroller#isScrolling`) - covering the common mobile pattern where a flick's native
   * momentum/inertial scroll keeps gliding well after the finger has already lifted. Checking
   * pointer-down alone is NOT sufficient: live-tested and confirmed a real bug where a sync echo
   * still arrived and got relayed during exactly this post-lift momentum window, since
   * `#pointersDown` was already back to empty by then even though the slide was still visibly
   * scrolling.
   *
   * Exists for external, cross-instance callers (e.g. `media-gallery.js`'s sync listener pair,
   * which keeps two independent clone-loop `Slideshow` instances' `current` in sync by replaying
   * each other's `SlideshowSelectEvent`s via `select()`) to avoid forcing a sync-triggered
   * `select()` onto an instance the user's gesture is still actively playing out on - a sync echo
   * arriving mid-gesture (finger down OR momentum still gliding) could yank that instance's scroll
   * position and `current` back to a stale value, fighting the user's own in-progress swipe
   * (observed as being unable to swipe past a certain slide - every attempt got interrupted). The
   * gesture that's actually happening will produce its own fresh `SlideshowSelectEvent` once it
   * genuinely settles, which syncs correctly on its own - so skipping (not queuing) a sync call
   * that arrives mid-gesture is the correct behavior, not a gap.
   * @type {boolean}
   */
  get gestureActive() {
    return this.#pointersDown.size > 0 || this.#dragging || Boolean(this.#scroll?.isScrolling);
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  /** Whether this slideshow uses clone-based infinite looping instead of live DOM
   * relocation. Only meaningful when `infinite` is also true. */
  get cloneLoop() {
    return this.hasAttribute('clone-loop');
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  // ---------------------------------------------------------------------------------------------
  // Transition state machine
  //
  // Every path that can change which slide is "current" - select()/next()/previous() (button,
  // keyboard, programmatic), pointer-drag release, and native touch/wheel/trackpad scroll settling
  // at a boundary - funnels through `#requestTransition()`. That is the ONLY place that ever moves
  // slide DOM order for a loop-wrap. `#activeTransition` is the ONLY flag tracking "a transition is
  // in flight"; there is exactly one queued follow-up request (`#queuedRequest`), holding only the
  // latest request so rapid repeated input resolves to wherever the user was most recently aiming.
  //
  // Boundary priming (`#primeBoundary()`) is architecturally distinct - it's prophylactic
  // "pre-stage a slide in case native scroll needs it", not a reaction to a completed navigation -
  // but it shares the same `SlideRelocation` primitive and MUST check `#activeTransition` (and
  // hold no relocation of its own while one is active) before it's allowed to touch DOM.
  // ---------------------------------------------------------------------------------------------

  /**
   * Non-null exactly while a transition (select/drag-wrap/scroll-boundary-wrap) is actively
   * moving DOM order and/or driving the scroller toward its target, up through that transition's
   * real, confirmed settle. This is the single flag every entry point checks before mutating
   * slide DOM order or starting a new scroll-driven transition - replacing the previous
   * `#disabled`/`#awaitingReorderSettle`/`#reorderSettleConsume` cluster, which was checked
   * inconsistently across call sites (the root cause of every interleaving bug found in this
   * feature's testing history).
   * @type {{ trigger: 'select' | 'drag' | 'scroll', relocation: SlideRelocation | null } | null}
   */
  #activeTransition = null;

  /**
   * The single queued follow-up request, recorded when a new request arrives while
   * `#activeTransition` is non-null. Only ever holds the LATEST such request - a newer request
   * supersedes an older queued one. Drained on a fresh microtask once the active transition's real
   * settle is confirmed (see `#drainQueue()`), never synchronously from inside the settle handler -
   * that handler runs from inside `Scroller`'s own scroll-event callback stack, and starting a new
   * transition's async work (DOM moves, `Scroller#to()` calls) before that outer scroll event has
   * fully finished unwinding risks reentering `Scroller`'s own state mid-update.
   * @type {SelectRequest | DragRequest | null}
   */
  #queuedRequest = null;

  /**
   * Non-null exactly while a clone-loop slideshow is resolving a landing on a boundary
   * clone back to the corresponding real slide (see `#resolveCloneLanding()`). Distinct
   * from `#activeTransition` (which drives the live-relocation reorder machinery) - a
   * `cloneLoop` slideshow never sets `#activeTransition` at all, it uses this instead.
   * @type {boolean}
   */
  #cloneLandingInFlight = false;

  /**
   * The single queued follow-up request for a clone-loop slideshow, recorded when
   * select()/next()/previous() is called while `#cloneLandingInFlight` is true. Same
   * latest-wins semantics and same fresh-microtask replay discipline as `#queuedRequest`
   * (never replay synchronously from inside the settle callback, to avoid reentering
   * `Scroller`'s own bookkeeping mid-update).
   * @type {SelectRequest | null}
   */
  #queuedCloneRequest = null;

  /**
   * Non-null exactly when a clone-loop scroll has most recently settled on a boundary clone and
   * that landing has NOT yet been corrected. Set by `#handleScroll`'s clone-loop branch on every
   * settle that lands on a clone (replacing whatever was previously noted here, including a note
   * for a different clone), cleared either by `#handleScroll` itself (if a later settle lands
   * somewhere else) or by `#maybeResolveCloneLanding()` once it actually performs the correction.
   *
   * ARCHITECTURAL CHANGE from the previous design: this state existing does NOT, by itself, mean
   * a corrective re-scroll is about to happen or is in flight - see `#cloneLandingInFlight` for
   * that. It only means "if we later gain confidence the user's gesture has ended while this is
   * still true, resolve THIS landing." Resting here indefinitely is an expected, normal state
   * (e.g. the user stopped dragging with their finger still down) - clones are indistinguishable
   * poster-frames of the real slide they represent, so resting on one is invisible to the user.
   * @type {{ clone: HTMLElement, previousIndex: number } | null}
   */
  #restingOnClone = null;

  /**
   * The set of pointer IDs currently physically down on `refs.scroller` - i.e. each pointer
   * between its own `pointerdown` and its corresponding `pointerup`/`pointercancel`/
   * `pointercapturelost`. Tracked per-pointer-ID (a `Set`, not a single boolean) so an incidental
   * second touch point during a swipe (an accidental second finger, stylus-adjacent contact -
   * `touch-action` isn't restricted anywhere in this gallery's CSS) can't fire its own
   * `pointerup` and prematurely signal "the gesture has ended" while the primary finger driving
   * the actual drag is still down; the gate this feeds (`#maybeResolveCloneLanding()`) only
   * treats the pointer as lifted once this set is genuinely empty.
   *
   * This is the direct, non-inferred gesture-end signal the redesigned clone-landing correction
   * is gated on: scroll-event quiet-time alone (the previous design's only signal) cannot
   * distinguish "browser done correcting" from "user still dragging, just between finger-movement
   * ticks" - a live capture proved settle firing while the finger was still down and drift
   * continuing ~650ms afterward, completely undetected. Tracked independently of
   * `#dragging`/`#handleMouseDown`'s own pointer-capture bookkeeping: that machinery is
   * exclusively for the desktop mouse-drag-to-scroll feature (only starts from a `mousedown`),
   * while raw touch/native-scroll-snap swiping - the actual gesture that lands on clones in
   * practice - never triggers `mousedown` and so never goes through `#dragging` at all. This is
   * the only place that native gesture's pointer lifecycle is observed.
   * @type {Set<number>}
   */
  #pointersDown = new Set();

  /**
   * Routes a request (from `select()` or drag-release) through the single serialized transition
   * queue: run it now if nothing is in flight, otherwise remember it as the latest pending
   * request and let `#drainQueue()` run it once the active transition settles.
   * @param {SelectRequest | DragRequest} request
   */
  #enqueueOrRun(request) {
    if (this.#activeTransition) {
      this.#queuedRequest = request;
      return;
    }

    this.#runRequest(request);
  }

  /**
   * Replays the queued request, if any, once the active transition's real settle is confirmed.
   * Always deferred to a fresh microtask (see `#queuedRequest`).
   */
  #drainQueue() {
    const request = this.#queuedRequest;
    if (!request) return;

    this.#queuedRequest = null;

    queueMicrotask(() => {
      // #activeTransition is guaranteed null here (we only just cleared it, and nothing runs
      // synchronously between that clear and this microtask), but route back through
      // #enqueueOrRun rather than #runRequest directly so this stays correct even if that ever
      // changes.
      this.#enqueueOrRun(request);
    });
  }

  /**
   * Executes a request immediately. Only ever called when `#activeTransition` is null: either
   * directly from `#enqueueOrRun()`, or from the queue drain above.
   * @param {SelectRequest | DragRequest} request
   */
  #runRequest(request) {
    if (request.kind === 'select') {
      this.#performSelect(request.input, request.event, request.options);
    } else {
      this.#performDragRelease(request);
    }
  }

  /**
   * The actual `select()` implementation, run only once it's this request's turn (see
   * `#enqueueOrRun()`/`#drainQueue()`). Resolves the requested slide against CURRENT logical
   * state - not whatever it was when the original `select()` call was made - since a queued
   * request may run after other state has changed. This includes the symbolic 'next'/'previous'
   * directions (see `next()`/`previous()`): they're resolved to an index HERE, against the
   * `current`/`visibleSlides` this method reads below, not eagerly by the caller - so a queued
   * next()/previous() correctly targets "one past wherever we ended up", not a frozen absolute
   * index computed before other state changed.
   * @param {number|string|{id: string}|'next'|'previous'} input
   * @param {Event} [event]
   * @param {{animate?: boolean}} [options]
   */
  async #performSelect(input, event, options = {}) {
    if (!this.refs.slides?.length || !this.#scroll) return;

    // Undo any proactive boundary priming before reading slides/current below - select() assumes
    // DOM order matches logical order, which a pending priming would violate.
    this.#restoreBoundaryPrime();

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (input === 'next') return this.nextIndex;
      if (input === 'previous') return this.previousIndex;
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides?.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;
    const targetSlide = slides[index];
    if (!targetSlide) return;

    const needsReorder = !instant && !isAdjacentSlide && visibleSlides.length === 1;

    if (needsReorder && currentSlide) {
      await this.#runReorderTransition({
        trigger: 'select',
        currentSlide,
        targetSlide,
        // Move targetSlide before or after currentSlide so we scroll a short distance in the
        // correct direction, matching the direction implied by the (unwrapped) requested index.
        placeBefore: requestedIndex < current,
        finalIndex: index,
        previousIndex: current,
        userInitiated: event != null,
        centerThumbnail: () => this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth'),
      });
      return;
    }

    const previousIndex = this.current;

    targetSlide.setAttribute('aria-hidden', 'false');
    this.#scroll.to(targetSlide, { instant });
    this.current = index;
    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide: targetSlide,
        id: targetSlide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * The clone-loop `select()` implementation. Every real slide is a permanent DOM
   * element, so - unlike the live-relocation path's `#performSelect()` - reaching any
   * target, adjacent or not, is always just a plain scroll; there is no separate reorder
   * transition to run. `next`/`previous` are resolved against CURRENT `current` here (not
   * eagerly by the caller), matching the same reasoning as `next()`/`previous()` below.
   *
   * Live-tested and confirmed a real bug in an earlier version of this method: unlike
   * `#resolveCloneLanding()` (which solves an almost-identical "land exactly on a specific
   * real slide, not a nearby clone" problem for native-scroll-driven boundary landings),
   * this method used to leave `scroll-snap-type` active during its own `to()` call and
   * never re-verified where the scroll actually settled - with `scroll-snap-type: x
   * mandatory` always active (see slideshow-styles.liquid), the browser's own snap
   * machinery is free to perform a corrective adjustment after an instant `scrollTo()`,
   * and can converge on a neighboring boundary clone instead of the intended real slide
   * (clones are built directly adjacent to the real slide they represent - see
   * `#buildBoundaryClones()`). Confirmed live via direct DOM inspection: `current`/the
   * dispatched index/a synced thumbnail rail all correctly reported the requested index,
   * while the actually-visible slide carried `data-clone`. `#handleScroll`'s clone-loop
   * branch can't catch this either - the corrective scroll runs under `Scroller`'s
   * `#ignore` flag (an instant, JS-initiated scroll), and `Scroller#handleScroll`'s
   * `#ignore` branch never reaches `Slideshow`'s own clone-detection logic at all.
   *
   * Fixed by reusing `#resolveCloneLanding()`'s established two-part pattern: disable snap
   * for the duration of the scroll (removing the browser's own corrective-tick opportunity
   * entirely), then re-measure live geometry after a real settle and correct course if the
   * scroll still ended up resting on a clone (same defense-in-depth loop
   * `#resolveCloneLanding()` already uses, since disabling snap makes a bad landing far
   * less likely but doesn't structurally guarantee it can never happen). This makes the
   * method `async` - see `select()`'s and `#drainCloneQueue()`'s call sites, both already
   * fire-and-forget (`select()` itself is `async` but nothing awaits this method's own
   * completion), so this doesn't change their observable behavior.
   * @param {number|string|{id: string}|'next'|'previous'} input
   * @param {Event} [event]
   * @param {{animate?: boolean}} [options]
   */
  async #performCloneLoopSelect(input, event, options = {}) {
    if (!this.refs.slides?.length || !this.#scroll) return;

    // Discard any pending "resting on clone, awaiting pointer-lift/settle confirmation" note (see
    // `#restingOnClone`) - this call is about to authoritatively re-target the scroll itself
    // (always to a real slide - `requestedIndex` below only ever resolves against `this.slides`),
    // so whatever the drag gesture was last resting on before this point is no longer relevant.
    // Without this, a settle debounce left over from that earlier drag (or a pointer-lift that
    // happens to arrive after this runs) could otherwise fire `#maybeResolveCloneLanding()`
    // against a stale clone and yank the scroll back to it out from under this select().
    this.#restingOnClone = null;

    // Store the actual current slide before any mutations, matching `#performSelect()`.
    const currentSlide = this.slides?.[this.current];

    // Clear any stale `reveal` (see the `{id}` branch below) before resolving the requested
    // index, matching `#performSelect()` - `this.slides` filters on `reveal`, so a leftover
    // `reveal` from a previous hidden-slide selection would skew index resolution here.
    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    let requestedIndex = (() => {
      if (input === 'next') return this.nextIndex;
      if (input === 'previous') return this.previousIndex;
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides?.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden, matching `#performSelect()`.
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    // `slides` is read fresh here, AFTER the reveal-resolution above, so it reflects any
    // just-revealed slide - matching the ordering `#performSelect()` relies on.
    const { slides } = this;

    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const lastIndex = slides.length - 1;
    let index = requestedIndex;
    if (!this.infinite) {
      index = clamp(requestedIndex, 0, lastIndex);
    } else if (requestedIndex < 0) {
      index = lastIndex;
    } else if (requestedIndex > lastIndex) {
      index = 0;
    }

    let targetSlide = slides[index];
    if (!targetSlide || targetSlide === currentSlide) return;

    const previousIndex = this.current;
    const { animate = true } = options;
    const instant = prefersReducedMotion() || !animate;

    this.#cloneLandingInFlight = true;

    // Disable scroll-snap for the duration of this scroll - see this method's doc comment for
    // why (mirrors `#resolveCloneLanding()`'s established pattern for the same problem).
    this.#scroll.snap = false;

    targetSlide.setAttribute('aria-hidden', 'false');
    this.#scroll.to(targetSlide, { instant });
    await this.#awaitScrollSettle();

    // Unconditionally restore snap, even on the early-return (disconnected) path below -
    // mirrors `#resolveCloneLanding()`'s own unconditional restore.
    if (this.#scroll) this.#scroll.snap = true;

    if (!this.isConnected || !this.#scroll) {
      this.#cloneLandingInFlight = false;
      return;
    }

    // Re-measure live geometry rather than trusting `targetSlide`: with snap disabled during
    // the scroll above, landing on a clone here should be rare, but re-verifying (rather than
    // trusting `targetSlide`/`index` unconditionally) is what actually guarantees `current`/the
    // dispatched event/a synced thumbnail rail can never drift from the true visible slide -
    // matching `#resolveCloneLanding()`'s own re-measure-and-correct loop.
    let settled = this.#closestCloneLoopSlide();
    while (settled?.hasAttribute('data-clone')) {
      const clonedRealIndex = this.#logicalIndexForCloneLoopSlide(settled);
      const clonedRealSlide = clonedRealIndex != null ? this.slides?.[clonedRealIndex] : undefined;
      if (!clonedRealSlide || clonedRealSlide === settled) break;

      this.#scroll.snap = false;
      this.#scroll.to(clonedRealSlide, { instant: true });
      await this.#awaitScrollSettle();
      if (this.#scroll) this.#scroll.snap = true;
      if (!this.isConnected || !this.#scroll) {
        this.#cloneLandingInFlight = false;
        return;
      }

      settled = this.#closestCloneLoopSlide();
    }

    const finalSlide = settled && !settled.hasAttribute('data-clone') ? settled : targetSlide;
    const finalRealIndex = this.slides?.indexOf(finalSlide);
    const finalIndex = finalRealIndex != null && finalRealIndex !== -1 ? finalRealIndex : index;
    targetSlide = finalSlide;

    targetSlide.setAttribute('aria-hidden', 'false');
    for (const slide of this.slides ?? []) {
      if (slide !== targetSlide) slide.setAttribute('aria-hidden', 'true');
    }
    this.current = finalIndex;
    this.#centerSelectedThumbnail(finalIndex, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: finalIndex,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide: targetSlide,
        id: targetSlide.getAttribute('slide-id'),
      })
    );

    this.#cloneLandingInFlight = false;
    this.#drainCloneQueue();
  }

  /**
   * Re-verifies the physical scroll position against `current` and silently corrects it if
   * they've drifted apart, without dispatching a `SlideshowSelectEvent` (nothing about logical
   * state changes here - only physical position, so nothing downstream needs to react).
   *
   * Exists for callers who need to force a landing check outside the normal
   * select()/drag/scroll flow, specifically because those flows' own corrections can be
   * defeated by zero-size layout: `#setupSlideshow()`'s initial positioning (see its rAF
   * callback) and `select()`'s same-index no-op guard (`#performCloneLoopSelect`'s
   * `targetSlide === currentSlide` check) both assume a real, laid-out geometry to measure or
   * correct against. A `<dialog>` not yet shown via `showModal()` has `display: none` (zero
   * size) by the UA stylesheet, so a `Slideshow` nested inside one that connects/initializes
   * while still hidden (e.g. `zoom-dialog.js`'s `zoomSlideshow`) can silently end up resting on
   * a boundary clone with no scroll event ever firing to self-correct, and later opening on the
   * slide `current` already reports (e.g. index 0, the common case) skips `select()`'s
   * corrective scroll entirely via that same-index guard - see `#buildBoundaryClones()`/
   * `#setupSlideshow()`'s rAF callback for the mechanism this is patching around. Call this once
   * geometry is actually valid (e.g. right after `dialog.showModal()`) to catch that case.
   *
   * No-ops entirely for non-clone-loop slideshows (nothing to verify), while a clone-landing
   * correction is already in flight (avoids fighting an in-progress one), or while the user's
   * pointer is down / the scroll is actively moving (avoid yanking an in-progress gesture -
   * matches `gestureActive`'s existing rationale elsewhere in this class).
   */
  verifyClonelessLanding() {
    if (!this.cloneLoop || !this.#scroll || this.#cloneLandingInFlight || this.gestureActive) return;

    const settled = this.#closestCloneLoopSlide();
    if (!settled) return;

    const currentSlide = this.slides?.[this.current];
    if (settled === currentSlide && !settled.hasAttribute('data-clone')) return;

    // Either resting on a clone, or resting on some real slide other than the one `current`
    // claims - either way, physical position and logical state disagree. Reposition to the
    // slide `current` actually points to (not to whatever `settled` measured), matching how
    // `#performCloneLoopSelect`'s same-index guard treats `current` as authoritative.
    const targetSlide = this.slides?.[this.current];
    if (!targetSlide) return;

    this.#scroll.to(targetSlide, { instant: true });
  }

  /**
   * Shared reorder-transition runner used by both the `select()` jump/wrap path and the
   * drag-release wrap path (previously two independent copies of the placeholder-swap trick).
   * Physically relocates `targetSlide` next to `currentSlide` via the shared `SlideRelocation`
   * primitive so the scroll animates a short distance, waits for that scroll's REAL settle,
   * restores logical DOM order, re-targets the scroll instantly to the slide's real (now
   * unchanged) position, waits for THAT settle too, and only then dispatches the select event and
   * clears `#activeTransition` - draining the queue afterward.
   * @param {Object} params
   * @param {'select' | 'drag'} params.trigger
   * @param {HTMLElement} params.currentSlide
   * @param {HTMLElement} params.targetSlide
   * @param {boolean} params.placeBefore - Relocate targetSlide before (vs after) currentSlide.
   * @param {number} params.finalIndex
   * @param {number} params.previousIndex
   * @param {boolean} params.userInitiated
   * @param {() => void} [params.centerThumbnail]
   */
  async #runReorderTransition({
    trigger,
    currentSlide,
    targetSlide,
    placeBefore,
    finalIndex,
    previousIndex,
    userInitiated,
    centerThumbnail,
  }) {
    if (!this.#scroll) return;

    // Claim #activeTransition BEFORE the first await below, not after - this call is only ever
    // reached with #activeTransition already null (callers only invoke it from #runRequest, which
    // #enqueueOrRun only calls when nothing is active), so there is no real transition to preserve
    // across the wait, only DOM-reorder eligibility for anything that might run during the await
    // (a `select()`/drag-start call, or a `#primeBoundary()` triggered by this same settle). Setting
    // it here, synchronously, closes that window entirely instead of leaving it open across an
    // `await` the way earlier revisions of this fix did.
    this.#activeTransition = { trigger, relocation: null };

    await this.#scroll.finished; // ensure we're not mid-scroll

    if (!this.isConnected || !this.#scroll) {
      this.#activeTransition = null;
      return;
    }

    const relocation = new SlideRelocation(targetSlide, currentSlide, placeBefore ? 'before' : 'after');
    this.#activeTransition = { trigger, relocation };

    if (this.current === 0) this.#scroll.to(currentSlide, { instant: true });

    // Animate the short hop toward the relocated slide, then wait for the REAL settle (not a
    // fixed delay - see `#awaitScrollSettle()`) before restoring DOM order. Restoring DOM order
    // and jumping to the slide's real position before this scroll has genuinely finished would
    // yank the animation mid-flight.
    this.#scroll.to(targetSlide);
    await this.#awaitScrollSettle();

    // It's possible the element was disconnected while we were awaiting (e.g. section re-render).
    if (!this.isConnected || !this.#scroll) {
      this.#activeTransition = null;
      return;
    }

    relocation.restore();

    // Explicitly own aria-hidden bookkeeping for this transition's outcome, rather than relying on
    // `#onTransitionEnd`'s `#updateVisibleSlides()` (skipped entirely while `#activeTransition` is
    // set, since its IntersectionObserver-cached source can reflect stale/transitional geometry
    // mid-transition - see `#onTransitionEnd`). Mark every other slide hidden, not just the target
    // visible, so a transition's final state is self-consistent even if the observer's next real
    // callback hasn't caught up yet.
    for (const slide of this.refs.slides ?? []) {
      slide.setAttribute('aria-hidden', slide === targetSlide ? 'false' : 'true');
    }

    // Instantly re-scroll to the slide's real (now restored) position, then wait for THAT
    // settle too before clearing #activeTransition - only once this final position is confirmed
    // is the DOM/scroll state actually done changing.
    this.#scroll.to(targetSlide, { instant: true });
    await this.#awaitScrollSettle();

    if (!this.isConnected) {
      this.#activeTransition = null;
      return;
    }

    this.current = finalIndex;
    centerThumbnail?.();

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: finalIndex,
        previousIndex,
        userInitiated,
        trigger,
        slide: targetSlide,
        id: targetSlide.getAttribute('slide-id'),
      })
    );

    // Force Safari to recalculate the timeline state on timeline refresh (after loop).
    requestAnimationFrame(() => {
      this.setAttribute('refreshing-timeline', '');
      requestAnimationFrame(() => {
        this.removeAttribute('refreshing-timeline');
      });
    });

    this.#activeTransition = null;
    this.#drainQueue();
  }

  /**
   * Waits for the scroller's current scroll to genuinely settle. Prefers `Scroller#finished`
   * (resolved by its real `scroll`-end debounce), but that promise resolves immediately if
   * `Scroller#to()` turned out to be a no-op (target position already matched current position
   * AND no scroll was already in flight - see `to()`'s `willChange` check in scrolling.js): a
   * real, reachable case here since a DOM relocation alone doesn't change scroll position. In
   * that case `#scroll.finished` resolves on THIS microtask turn rather than after a real
   * settle event, which would let the caller proceed before the DOM/scroll state has actually
   * converged. Confirm real settlement by also waiting one animation frame plus a microtask,
   * comfortably past the point any real `scroll` event for an instant scroll would have fired,
   * before returning.
   */
  async #awaitScrollSettle() {
    if (!this.#scroll) return;
    await this.#scroll.finished;
    await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
  }

  /**
   * Handles the 'pointerup' drag-release request once it's this request's turn to run (see
   * `#enqueueOrRun()`). Re-derives current/next against CURRENT logical state rather than trusting
   * values captured back at pointerup time, since a queued drag-release may run after other state
   * (e.g. a queued select()) has changed things - though in practice `#handleMouseDown` refuses to
   * start a new drag while a transition is active, so this mainly matters for the drag that was
   * already released and queued behind another in-flight transition.
   * @param {DragRequest} request
   */
  async #performDragRelease(request) {
    const { velocity, distanceTravelled, pointerId } = request;
    if (!this.#scroll) return;

    this.#restoreBoundaryPrime();

    // Discard any pending "resting on clone" note - see the matching comment in
    // `#performCloneLoopSelect()`. This is the desktop mouse-drag-to-scroll release path
    // (`#handleMouseDown`); every branch below re-targets the scroll itself directly (to a real
    // slide), so a stale note from earlier in this same gesture (or a previous one) must not be
    // allowed to trigger a correction against a clone the drag is no longer near.
    if (this.cloneLoop) this.#restingOnClone = null;

    const { current, slides } = this;
    if (!slides?.length) return;

    const direction = Math.sign(velocity);
    const next = this.#sync();

    const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
    const rawIndex = next + modifier;
    const wrapped = this.infinite && (rawIndex < 0 || rawIndex > slides.length - 1);
    const newIndex = this.infinite
      ? (rawIndex + slides.length) % slides.length
      : clamp(rawIndex, 0, slides.length - 1);

    const newSlide = slides[newIndex];
    const currentIndex = this.current;
    const currentSlide = slides[currentIndex];

    if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

    const finishDrag = () => {
      this.removeAttribute('dragging');
      if (pointerId != null) this.releasePointerCapture(pointerId);
    };

    if (wrapped && currentSlide && !this.cloneLoop) {
      finishDrag();
      await this.#runReorderTransition({
        trigger: 'drag',
        currentSlide,
        targetSlide: newSlide,
        placeBefore: rawIndex < 0,
        finalIndex: newIndex,
        previousIndex: currentIndex,
        userInitiated: true,
        centerThumbnail: () => this.#centerSelectedThumbnail(newIndex),
      });
    } else if (wrapped && currentSlide && this.cloneLoop) {
      // Clone-loop: dragging past a boundary is just a plain scroll to the real target
      // slide (every real slide always exists in the track) - no reorder transition
      // needed. If the drag released while still visually over a clone, #handleScroll's
      // clone branch will resolve it on settle same as a native swipe would.
      finishDrag();
      this.#scroll.to(newSlide);
      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;
    } else {
      this.#scroll.to(newSlide);
      finishDrag();
      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;
    }

    await this.#scroll.finished;

    // It's possible that the user started dragging again before the scroll finished
    if (this.#dragging) return;

    this.#scroll.snap = true;
    this.resume();
  }

  /**
   * Tracks a slide proactively relocated by `#primeBoundary()` so that native touch/wheel/
   * trackpad scrolling (which cannot scroll past DOM that doesn't exist) has somewhere to go when
   * the user is at the first/last slide of an infinite slideshow. Set by `#primeBoundary()` and
   * cleared by `#restoreBoundaryPrime()`. Distinct from `#activeTransition`: this is prophylactic
   * staging, not a navigation in progress, and `#primeBoundary()` itself checks `#activeTransition`
   * (see there) before ever setting this.
   * @type {{ edge: 'start' | 'end', relocation: SlideRelocation } | null}
   */
  #boundaryPrime = null;

  /**
   * Timestamp (`performance.now()`) of the last time a primed landing was resolved (see
   * `#handleScroll`). Used by `#primeBoundary()` as a circuit breaker: if landings are resolving
   * faster than a real user could scroll, something is re-entrantly cycling priming/landing rather
   * than responding to genuine navigation, so priming is skipped for one cycle to let things settle
   * instead of relocating DOM again. Defensive only - not a substitute for keeping the underlying
   * prime/land sequence itself correct.
   * @type {number}
   */
  #lastPrimedLandingAt = 0;

  /**
   * Minimum real time (ms) that must elapse between two primed-landing resolutions before
   * `#primeBoundary()` will prime again. Comfortably below any plausible human scroll cadence
   * (native scroll-end settling already takes at least `SCROLL_END_TIMEOUT` in scrolling.js), so it
   * never interferes with legitimate fast navigation, only genuine same-tick re-entrant cycling.
   * @type {number}
   */
  static #PRIME_MIN_INTERVAL_MS = 16;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * Type is widened to include `undefined` because `disconnectedCallback()` explicitly nulls
   * this out after destroying it, so it reads falsy while genuinely unavailable.
   * @type {Scroller|undefined}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * The two boundary clone slides appended to the track when `cloneLoop` is active - a
   * clone of the last real slide before the first real slide, and a clone of the first
   * real slide after the last real slide. Populated by `#buildBoundaryClones()`, removed
   * by `#removeBoundaryClones()`. Empty when `cloneLoop` is false/inapplicable (fewer
   * than 2 real slides).
   * @type {HTMLElement[]}
   */
  #boundaryClones = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    this.#buildBoundaryClones();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    if (this.cloneLoop) {
      // Direct gesture-end signal for the redesigned clone-landing correction (see
      // `#pointersDown`/`#maybeResolveCloneLanding()`) - only meaningful, and only wired up, for
      // clone-loop slideshows, since nothing else consults it. `pointerdown` covers touch, mouse,
      // and pen alike. The lift event set (`pointerup`/`pointercancel`/`pointercapturelost`)
      // intentionally matches the exhaustive set `#handleMouseDown`'s own drag-release cleanup
      // already uses, for consistency.
      //
      // Listened on `this` (the Slideshow element itself), NOT `scroller` - live-tested and
      // confirmed a real bug from listening on `scroller`: `#handleMouseDown`'s own drag
      // machinery calls `this.setPointerCapture(event.pointerId)` on THIS element once a mouse
      // drag starts moving, which (per the Pointer Events spec) reroutes that pointer's
      // subsequent events to dispatch with `this` as their target instead of following normal
      // hit-testing - they no longer bubble through `scroller` as a descendant at all, so a
      // `pointerup`/`pointercancel` listener scoped to `scroller` silently never fires for that
      // pointer once capture is set. `#pointersDown` was left permanently stuck non-empty after
      // the very first mouse drag, and `#maybeResolveCloneLanding()`'s gate (which requires it to
      // reach empty) never passed again for the rest of the session. Listening on `this` matches
      // where capture is actually set, so the event still dispatches here regardless of capture -
      // capture reroutes the effective target, it doesn't stop the event bubbling through the
      // capturing element and its ancestors (confirmed by `document`-level listeners elsewhere in
      // this class already working correctly through capture).
      this.addEventListener('pointerdown', this.#handlePointerDown);
      this.addEventListener('pointerup', this.#handlePointerLift);
      this.addEventListener('pointercancel', this.#handlePointerLift);
      this.addEventListener('pointercapturelost', this.#handlePointerLift);
    }

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else if (this.cloneLoop && this.#boundaryClones.length > 0) {
          // A leading boundary clone sits before the real first slide in DOM order (see
          // `#buildBoundaryClones()`), so the browser's own default, un-scrolled resting
          // position (scrollLeft: 0) lands ON that clone, not on the real initial slide -
          // even though `this.current` was already set to `initialSlideIndex` above. The
          // two general-purpose corrections below can't catch this specific case: (1)
          // `select(0, ...)` no-ops because `#performCloneLoopSelect`'s same-slide guard
          // compares logical indices (`targetSlide === currentSlide`), and `current` already
          // reads as the target index; (2) `#updateVisibleSlides()`'s zero-visible-slides
          // fallback only self-corrects a `visibleSlides` reading of `this.slides` (which
          // excludes clones by construction) - but by the time this rAF callback runs, the
          // IntersectionObserver's very first callback may already have fired and reported
          // the real initial slide as visible if layout allows it, making that fallback
          // unreliable to depend on here too. Bypass `select()` and reposition the physical
          // scroll directly, the same way `#resolveCloneLanding()`/`#performCloneLoopSelect()`
          // do for their own corrective re-scrolls.
          const realInitialSlide = this.initialSlide;
          if (realInitialSlide) this.#scroll.to(realInitialSlide, { instant: true });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }

        // Covers the same "connected while `display: none`, so the initial clone-landing
        // correction above couldn't measure real geometry" hazard as `open()`'s call in
        // zoom-dialog.js (see `verifyClonelessLanding()`'s doc comment) - but for the MAIN
        // gallery slideshow specifically, not just the zoom dialog. In `hero`/`grid`
        // presentation, this element is CSS-hidden on desktop viewports
        // (product-media-gallery-content-styles.liquid), so a desktop pageview connects and
        // runs #setupSlideshow() entirely while `display: none` - not just a resize corner
        // case, but the default desktop experience for those presentations. `ResizeObserver`
        // fires when a `display: none` element becomes visible (its box goes from 0x0 to
        // real dimensions) as well as on any later viewport-crossing resize, so this single
        // call site covers both connect-while-hidden and later become-visible transitions.
        // Cheap and safe to call unconditionally on every resize tick: no-ops immediately for
        // non-clone-loop slideshows, when already correctly landed, or mid-gesture.
        this.verifyClonelessLanding();
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Builds a poster-frame clone of a slide for clone-loop boundary padding: an
   * `<slideshow-slide>` containing only static image content (never a live second
   * `<video>`/`<model-viewer>` instance - see spec design §1). Reuses the source
   * slide's top-level `<picture>` or `<img>` if present, cloning it wholesale -
   * this matters because `product-media.liquid`/`product-media-mood-image.liquid`'s
   * `mobile_only` mode renders a `<picture><source media="..." srcset="..."><img>
   * (no src)</picture>` wrapper where the real image data lives on the `<source>`,
   * not the fallback `<img>`; cloning only the inner `<img>` would silently discard
   * it. Produces an empty clone if the source slide has neither (not expected to
   * occur for image-only slides in practice; not yet exercised by any live consumer).
   * @param {HTMLElement} sourceSlide
   * @returns {HTMLElement}
   */
  #buildBoundaryClone(sourceSlide) {
    const clone = /** @type {HTMLElement} */ (document.createElement('slideshow-slide'));
    clone.className = sourceSlide.className;
    clone.setAttribute('style', sourceSlide.getAttribute('style') ?? '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.setAttribute('data-clone', '');

    // Prefer a `<picture>` wrapper over a bare `<img>` when both would otherwise
    // match: a `mobile_only`-rendered slide's `<picture>` contains its own
    // (deliberately src-less) fallback `<img>`, so a plain `querySelector('img')`
    // would find that inner img first and clone only it, discarding the enclosing
    // `<picture>`/`<source>` that actually carries the image data. Cloning the
    // `<picture>` wholesale via `cloneNode(true)` preserves the `<source>` intact.
    const sourceMedia = sourceSlide.querySelector('picture, img');
    if (sourceMedia) {
      const clonedMedia = /** @type {HTMLElement} */ (sourceMedia.cloneNode(true));
      // The actual <img> to sanitize below: the clone itself if it's a bare img,
      // or the <img> nested inside the cloned <picture>. `<source>` elements are
      // left completely untouched - their srcset/media/sizes must survive as-is.
      const img =
        clonedMedia instanceof HTMLImageElement ? clonedMedia : clonedMedia.querySelector('img');

      if (img) {
        // Clones are never the LCP element and never need eager priority - see spec
        // design §3. Strip any ref attribute too: refs are unique per Component, and a
        // duplicate `ref="image"` would confuse the MutationObserver-backed refs system.
        img.removeAttribute('fetchpriority');
        img.setAttribute('loading', 'lazy');
        img.removeAttribute('ref');
        img.removeAttribute('id');
      }

      clone.appendChild(clonedMedia);
    }

    return clone;
  }

  /**
   * Appends the two boundary clones (see `#buildBoundaryClone()`) to the track: a clone
   * of the last real slide before the first, a clone of the first real slide after the
   * last. No-ops when `cloneLoop` is off, fewer than 2 real slides exist, or clones
   * already exist (idempotent - safe to call from setup unconditionally).
   */
  #buildBoundaryClones() {
    if (!this.cloneLoop || !this.infinite) return;
    if (this.#boundaryClones.length > 0) return;

    const { slides } = this;
    if (!slides || slides.length < 2) return;

    const firstSlide = slides[0];
    const lastSlide = slides[slides.length - 1];
    if (!firstSlide || !lastSlide) return;

    const leadingClone = this.#buildBoundaryClone(lastSlide);
    const trailingClone = this.#buildBoundaryClone(firstSlide);

    firstSlide.before(leadingClone);
    lastSlide.after(trailingClone);

    this.#boundaryClones = [leadingClone, trailingClone];
  }

  /** Removes any boundary clones built by `#buildBoundaryClones()`. Safe to call
   * unconditionally (e.g. from `disconnectedCallback`). */
  #removeBoundaryClones() {
    for (const clone of this.#boundaryClones) {
      clone.remove();
    }
    this.#boundaryClones = [];
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;

    if (this.cloneLoop) {
      // Clone-loop slideshows use a completely different, much simpler landing model:
      // native scroll-snap can land on a real slide OR a boundary clone, both of which
      // are ordinary elements sitting in the track the whole time - no relocation ever
      // happens during the gesture itself. Only skip while a landing resolution's own
      // corrective re-scroll (see `#resolveCloneLanding()`) is already in flight, for the
      // same reentrancy reason `#activeTransition` guards the live-relocation path below.
      if (this.#cloneLandingInFlight) return;

      const closest = this.#closestCloneLoopSlide();
      if (!closest) return;

      if (closest.hasAttribute('data-clone')) {
        // ARCHITECTURAL CHANGE (see #resolveCloneLanding()/#maybeResolveCloneLanding() docs):
        // landing on a clone no longer triggers an immediate corrective re-scroll. A clone is a
        // visually-identical poster-frame of the real slide it represents, so resting on one is
        // invisible to the user - it's safe to just note it and let native scroll-snap keep
        // handling the gesture (including further dragging off the clone) until we have high
        // confidence the user's physical gesture has actually, fully ended.
        //
        // Deliberately does NOT attempt to resolve from here: `#handleScroll` (this method) is
        // `Scroller`'s `onScroll` callback, which fires BOTH on genuine settle AND repeatedly
        // (throttled) while a scroll is still actively in progress - it cannot itself distinguish
        // the two (`Scroller#isScrolling` reads `true` in both cases from inside this callback,
        // since the settle case only flips it to `false` after `onScroll` already returned - see
        // scrolling.js). The one signal that reliably fires exactly once per genuine settle,
        // after that flip, is `onScrollEnd` (`#onTransitionEnd` below) - that's where the actual
        // gate check (`#maybeResolveCloneLanding()`) lives. Just record the note here.
        this.#restingOnClone = { clone: closest, previousIndex };
        return;
      }

      // Settled on a real slide - clear any stale resting-on-clone note (e.g. the user dragged
      // back off the clone before lifting) so a later pointer-lift doesn't try to resolve a
      // landing that's no longer current.
      this.#restingOnClone = null;

      const index = this.slides?.indexOf(closest);
      if (index == null || index === -1 || index === previousIndex) return;

      this.current = index;
      this.dispatchEvent(
        new SlideshowSelectEvent({
          index,
          previousIndex,
          userInitiated: true,
          trigger: 'scroll',
          slide: closest,
          id: closest.getAttribute('slide-id'),
        })
      );
      return;
    }

    // A reorder transition (`#runReorderTransition`) is driving the scroller itself through a
    // sequence of intermediate positions (relocated slide -> restored slide) while DOM order is
    // temporarily non-logical - `#sync()`/`#landedOnPrimedSlide()` below would read that
    // transitional geometry and dispatch a wrong-index select event, racing ahead of and bypassing
    // the transition's own completion logic entirely (confirmed live: this is what let a native
    // `trigger=scroll` event resolve a slide's `current`/dispatch out from under an in-flight
    // `trigger=select` reorder transition that owned that exact scroll, leaving `#activeTransition`
    // cleared without ever reaching `#runReorderTransition`'s own completion code - the transition
    // itself doesn't drive `#handleScroll`, only `#onTransitionInit`/`#onTransitionEnd`/its own
    // explicit `await`s, so without this check nothing here knew a transition already owned this
    // scroll). The transition's own logic is the sole source of truth for its own scroll sequence;
    // let it finish untouched.
    if (this.#activeTransition) return;

    // If a boundary slide was proactively relocated by `#primeBoundary()`, check whether this
    // scroll landed on it (i.e. the user swiped/scrolled natively past the real first/last
    // slide) *before* syncing/dispatching normally. `#sync()` would otherwise report the
    // relocated slide's transient DOM position (its index while parked next to the opposite
    // boundary) rather than its real logical index, firing a momentarily-wrong select event
    // that flashes consumers like the zoom-dialog thumbnail rail to the wrong thumbnail. Resolve
    // the priming first so only the corrected index is ever dispatched.
    if (this.#boundaryPrime && this.#landedOnPrimedSlide()) {
      this.#resolvePrimedLanding(previousIndex);
      return;
    }

    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.removeAttribute('transitioning');

    // While a transition (select/drag-wrap reorder) is actively driving scroll toward its own
    // target, its `#awaitScrollSettle()` call is the one consuming these settle events - boundary
    // priming must not react to that transitional scroll state as if it were a fresh, genuine
    // boundary arrival. `#primeBoundary()` itself re-checks `#activeTransition`, but skip the call
    // entirely here too so a mid-transition settle can never even attempt it.
    //
    // `#updateVisibleSlides()` (which writes `aria-hidden` from the IntersectionObserver's cached,
    // asynchronously-delivered `visibleSlides` snapshot) is skipped here too for the same reason:
    // during a reorder transition's intermediate settles, that cache can reflect transitional
    // (relocated) DOM geometry, and nothing re-syncs it once the transition lands on its real final
    // slide - `#runReorderTransition`/`#resolvePrimedLanding` already explicitly set the correct
    // slide's `aria-hidden` themselves on completion, so an observer callback firing out of order
    // during the transition must not be allowed to clobber that with stale visibility (confirmed
    // live via a DevTools snapshot: `current`/the dispatched select index correctly pointed at one
    // slide while `aria-hidden="false"` - the actual visible slide - pointed at a different one).
    //
    // `#cloneLandingInFlight` guards the same race for the clone-loop landing path
    // (`#resolveCloneLanding()`): a clone-loop slideshow never sets `#activeTransition`, but its
    // own intermediate settle during `#resolveCloneLanding()`'s instant re-scroll is just as
    // capable of triggering a stale `#updateVisibleSlides()` call that clobbers the explicit
    // `aria-hidden` sweep `#resolveCloneLanding()` performs on completion.
    if (this.#activeTransition || this.#cloneLandingInFlight) return;

    // `onScrollEnd` fires exactly once per genuine settle episode - unlike `#handleScroll`
    // (`onScroll`), which also fires repeatedly (throttled) while a scroll is still actively in
    // progress and so cannot itself distinguish "just settled" from "mid-gesture tick" (see the
    // comment in `#handleScroll`'s clone-loop branch). This is therefore the correct, single
    // place to attempt the redesigned clone-landing correction: every genuine settle re-checks
    // whether a noted `#restingOnClone` can now be resolved (pointer already lifted + this
    // settle IS the "scroll genuinely stopped" confirmation) - see `#maybeResolveCloneLanding()`.
    if (this.cloneLoop) this.#maybeResolveCloneLanding('settle');

    this.#updateVisibleSlides();
    if (!this.cloneLoop) this.#primeBoundary();
  };

  /**
   * Checks (without mutating state) whether the scroll that just settled landed on the slide
   * currently parked by `#primeBoundary()`.
   *
   * Deliberately does NOT rely on the cached `this.visibleSlides` (populated asynchronously by
   * the IntersectionObserver): `#primeBoundary()`/`#resolvePrimedLanding()` themselves perform
   * synchronous DOM moves followed by instant re-scrolls, and the observer can easily not have
   * delivered a fresh snapshot reflecting those moves by the time the next scroll-end fires -
   * using the stale cache here was the root cause of a self-perpetuating priming/landing race
   * (see #sync() for the matching fix). `#closestSlide()` re-measures live geometry instead, so
   * it can never be stale relative to the DOM/scroll state that just settled.
   * @returns {boolean}
   */
  #landedOnPrimedSlide() {
    const primed = this.#boundaryPrime;
    if (!primed) return false;

    return this.#closestSlide() === primed.relocation.slide;
  }

  /**
   * Restores logical DOM order for a slide proactively relocated by `#primeBoundary()`, once the
   * user has actually scrolled onto it, and corrects `current` (and dispatches the single
   * `SlideshowSelectEvent` for this transition) to the real logical index. This is the "landing"
   * half of the proactive loop trick described on `#primeBoundary()`. Runs its own re-scroll
   * through the same `#activeTransition`-gated pattern as a reactive transition, so anything else
   * that might otherwise fire mid-resolution (a queued select, a fresh drag-start) correctly waits
   * instead of interleaving with it.
   * @param {number} previousIndex - The slide index the user was on before this scroll, captured
   *   before `#sync()`/DOM restoration could overwrite `this.current`.
   */
  async #resolvePrimedLanding(previousIndex) {
    const primed = this.#boundaryPrime;
    if (!primed || !this.#scroll) return;

    this.#boundaryPrime = null;
    this.#lastPrimedLandingAt = performance.now();
    this.#activeTransition = { trigger: 'scroll', relocation: primed.relocation };

    const { relocation, edge } = primed;
    const { slide } = relocation;

    // Move the slide back to its logical position and instantly re-target the scroll there,
    // since its on-screen position has changed as a result of the DOM move. Wait for the REAL
    // settle (not a fixed delay) before clearing #activeTransition/dispatching - see
    // `#awaitScrollSettle()`.
    relocation.restore();
    this.#scroll.to(slide, { instant: true });
    await this.#awaitScrollSettle();

    if (!this.isConnected || !this.#scroll) {
      this.#activeTransition = null;
      return;
    }

    // 'end' priming parked the first slide (logical index 0) after the last slide; 'start'
    // priming parked the last slide (logical index `length - 1`) before the first slide.
    const slides = this.slides;
    const index = edge === 'end' ? 0 : (slides?.length ?? 1) - 1;
    this.current = index;

    // Explicitly own aria-hidden bookkeeping for this landing's outcome - see the matching comment
    // in `#runReorderTransition` for why `#onTransitionEnd`'s `#updateVisibleSlides()` can't be
    // trusted to do this correctly while `#activeTransition` was set.
    for (const s of this.refs.slides ?? []) {
      s.setAttribute('aria-hidden', s === slide ? 'false' : 'true');
    }

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );

    // Force Safari to recalculate the timeline state on timeline refresh (after loop),
    // matching the same fixup used by the reactive select()/drag-release reorder tricks.
    requestAnimationFrame(() => {
      this.setAttribute('refreshing-timeline', '');
      requestAnimationFrame(() => {
        this.removeAttribute('refreshing-timeline');
      });
    });

    this.#activeTransition = null;
    this.#drainQueue();
  }

  /**
   * Performs the actual clone-landing correction: an instant, invisible reposition from a
   * boundary clone to the corresponding real slide at the opposite end, confirmed via a real
   * settle (`#awaitScrollSettle()`), then dispatches the single `SlideshowSelectEvent` for this
   * navigation. Unlike the live-relocation reorder transition, no DOM ever moves here - the
   * clone and the real slide are both already permanently in the track.
   *
   * Only ever called from `#maybeResolveCloneLanding()`, once that method's gate has confirmed
   * high confidence that the user's physical gesture has genuinely, fully ended (pointer lifted
   * AND scroll genuinely settled - see there for the full trigger design and why landing on a
   * clone no longer triggers this eagerly/immediately the way it used to).
   *
   * IMPORTANT: the corrective scroll issued below is held under `Scroller`'s `#ignore` flag for
   * its ENTIRE settle episode, which is correct and must stay that way (it's what makes repeated
   * native snap-correction ticks after an instant `to()` resolve as one settle instead of each
   * looking like a fresh user scroll - see the `snap` setter / `#handleScroll`'s `#ignore` branch
   * in scrolling.js). `#maybeResolveCloneLanding()`'s pointer-lift + scroll-settled gate means
   * this method is now only ever entered once the user's gesture has already, genuinely ended -
   * but a NEW gesture (a fresh drag/swipe) could in principle start again during this method's
   * own corrective `await`, and native momentum immediately following a very fast re-swipe could
   * still carry the scroll past this correction's target before it settles. So rather than
   * trusting `targetSlide`/`targetIndex` (computed BEFORE the await, from whichever clone is
   * under consideration), re-measure the live scroll position via `#closestCloneLoopSlide()`
   * AFTER the settle to find out where the scroll actually, finally rests - looping if that
   * landed on another clone (the same one bounced back onto, or the opposite one), so drift that
   * crosses a boundary more than once still resolves correctly rather than resolving to a stale
   * clone.
   * @param {HTMLElement} cloneSlide
   * @param {number} previousIndex
   */
  async #resolveCloneLanding(cloneSlide, previousIndex) {
    if (!this.#scroll) return;

    this.#cloneLandingInFlight = true;

    // Disable scroll-snap for the duration of this corrective scroll, same as the existing
    // drag-gesture pattern (see `#handleMouseDown`/`#performDragRelease`). With snap left active,
    // the browser's own snap machinery can need a further corrective tick to converge on the
    // target after this instant scroll, and that extra tick could otherwise be misread as fresh
    // user input (see `Scroller#handleScroll`'s `#ignore` handling in scrolling.js).
    this.#scroll.snap = false;

    /** @type {HTMLElement | undefined} */
    let landingSlide;
    /** @type {number | undefined} */
    let landingIndex;
    let currentClone = cloneSlide;

    // Loop rather than recurse-once: each corrective `to()` below targets whatever the clone
    // currently under consideration maps to, then re-measures for real after the settle. If the
    // drift that occurred DURING that settle happened to cross another boundary and land back on
    // a clone (same or opposite), re-resolve against THAT clone in turn, until live geometry
    // finally confirms a real slide. A real `scroll` settle occurred each iteration (that's what
    // `#awaitScrollSettle()` waits for), so this cannot spin without genuine scroll activity
    // driving it.
    for (;;) {
      const targetIndex = this.#logicalIndexForCloneLoopSlide(currentClone);
      const { slides } = this;
      if (targetIndex == null || !slides) {
        this.#cloneLandingInFlight = false;
        this.#scroll.snap = true;
        return;
      }
      const targetSlide = slides[targetIndex];
      if (!targetSlide) {
        this.#cloneLandingInFlight = false;
        this.#scroll.snap = true;
        return;
      }

      this.#scroll.to(targetSlide, { instant: true });
      await this.#awaitScrollSettle();

      if (!this.isConnected || !this.#scroll) {
        this.#cloneLandingInFlight = false;
        if (this.#scroll) this.#scroll.snap = true;
        return;
      }

      // Re-measure live geometry rather than trusting `targetSlide`: genuinely continued user
      // scrolling during the await above (see the method doc) can leave the scroller resting
      // somewhere other than where this corrective scroll originally aimed.
      const settled = this.#closestCloneLoopSlide();

      if (!settled) {
        // Couldn't measure (e.g. disconnected mid-loop) - fall back to the last computed target
        // rather than leaving state unresolved.
        landingSlide = targetSlide;
        landingIndex = targetIndex;
        break;
      }

      if (settled.hasAttribute('data-clone')) {
        // Drift carried the scroll onto a clone (same one or the opposite one) - resolve THAT
        // landing in turn rather than accepting the stale, pre-drift target.
        currentClone = settled;
        continue;
      }

      // Settled on a real slide - this is the true final landing.
      const realIndex = this.slides?.indexOf(settled);
      landingSlide = settled;
      landingIndex = realIndex != null && realIndex !== -1 ? realIndex : targetIndex;
      break;
    }

    // Unconditionally restore snap, even on an early-return (disconnected) path below - mirrors
    // `#performDragRelease`, which always re-enables snap before returning. Nothing else would
    // otherwise re-enable it here since, unlike the "user started a new drag" early return in
    // `#performDragRelease`, no other in-flight lifecycle owns re-enabling it for this method.
    this.#scroll.snap = true;

    if (!this.isConnected || !this.#scroll || landingSlide == null || landingIndex == null) {
      this.#cloneLandingInFlight = false;
      return;
    }

    const targetSlide = landingSlide;
    const targetIndex = landingIndex;
    const { slides } = this;

    this.current = targetIndex;
    targetSlide.setAttribute('aria-hidden', 'false');
    for (const slide of slides ?? []) {
      if (slide !== targetSlide) slide.setAttribute('aria-hidden', 'true');
    }
    this.#centerSelectedThumbnail(targetIndex, 'instant');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: targetIndex,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide: targetSlide,
        id: targetSlide.getAttribute('slide-id'),
      })
    );

    // Force Safari to recalculate the timeline state on timeline refresh (after loop),
    // matching the same fixup used by the live-relocation reorder path.
    requestAnimationFrame(() => {
      this.setAttribute('refreshing-timeline', '');
      requestAnimationFrame(() => {
        this.removeAttribute('refreshing-timeline');
      });
    });

    this.#cloneLandingInFlight = false;
    this.#drainCloneQueue();
  }

  /** Replays the queued clone-loop request, if any, on a fresh microtask - see
   * `#queuedCloneRequest`. */
  #drainCloneQueue() {
    const request = this.#queuedCloneRequest;
    if (!request) return;

    this.#queuedCloneRequest = null;

    queueMicrotask(() => {
      this.#performCloneLoopSelect(request.input, request.event, request.options);
    });
  }

  /**
   * Proactively relocates a slide so that native touch/wheel/trackpad scrolling has somewhere to
   * scroll to when the slideshow is infinite and sitting at the first or last slide. Native
   * scroll-snap can only scroll within DOM elements that already exist in the track - it cannot
   * "loop" the way `select()`/drag-release do by reacting to a completed navigation, because there
   * is no equivalent "the user tried to go further" event for native scroll. So instead of
   * reacting after the fact, this runs on every scroll-end and, when at a boundary, moves the real
   * opposite-end slide (not a clone - slides can contain <model-viewer>/<video>/deferred-media
   * that must not be duplicated live) to sit adjacent to the current boundary slide, using the
   * same `SlideRelocation` primitive as the reactive reorder transitions. The move itself does not
   * change scroll position, so it does not fire a scroll event and cannot re-trigger itself.
   * `#resolvePrimedLanding()` above undoes it once the user actually scrolls onto the relocated
   * slide; `#restoreBoundaryPrime()` undoes it if the user instead navigates via select()/pointer-
   * drag before using it, or scrolls back away from the boundary.
   *
   * MUST NOT run while `#activeTransition` is set - both the caller (`#onTransitionEnd`) and this
   * method itself check it, so a mid-transition settle event can never cause a prime.
   */
  #primeBoundary() {
    if (this.#activeTransition) return;
    if (!this.infinite || this.#dragging || this.disabled) return;

    // Circuit breaker: if a landing just resolved faster than a real user could have scrolled,
    // something is re-entrantly cycling prime/land rather than responding to genuine navigation.
    // Skip priming this cycle instead of relocating DOM again, so any such cycle can't sustain
    // itself indefinitely. See `#lastPrimedLandingAt`.
    if (performance.now() - this.#lastPrimedLandingAt < Slideshow.#PRIME_MIN_INTERVAL_MS) {
      return;
    }

    // Only meaningful for one-slide-at-a-time carousels, matching the same restriction the
    // reactive reorder transitions already apply (see `visibleSlides.length === 1` there).
    // Multi-visible-slide layouts don't have a single well-defined "next" slide to park
    // adjacently, and don't animate a short hop on wrap either.
    if (this.visibleSlides.length > 1) {
      this.#restoreBoundaryPrime();
      return;
    }

    const { slides } = this;
    if (!slides || slides.length <= 1) return;

    if (!this.atStart && !this.atEnd) {
      // No longer at a boundary (e.g. user scrolled back toward the middle) - nothing to prime.
      this.#restoreBoundaryPrime();
      return;
    }

    const targetEdge = this.atEnd ? 'end' : 'start';
    if (this.#boundaryPrime?.edge === targetEdge) return;
    this.#restoreBoundaryPrime();

    const firstSlide = slides[0];
    const lastSlide = slides[slides.length - 1];
    if (!firstSlide || !lastSlide || firstSlide === lastSlide) return;

    const relocation =
      targetEdge === 'end'
        ? new SlideRelocation(firstSlide, lastSlide, 'after')
        : new SlideRelocation(lastSlide, firstSlide, 'before');

    this.#boundaryPrime = { edge: targetEdge, relocation };

    // Deliberately leave `this.#current` untouched: it indexes the logical slide order that
    // dots/thumbnails/prev-next-disabled state are keyed against (see the `current` setter),
    // not the live DOM order. `atStart`/`atEnd` only use it as a boundary boolean, which stays
    // correct here since neither the current slide's identity, visibility, nor scroll position
    // changed - only an unrelated (off-screen) slide moved. Anything that needs to index
    // `this.slides` by `current` (select(), pointer-drag) calls `#restoreBoundaryPrime()` first,
    // which restores logical DOM order before they read it.
  }

  /**
   * Restores a slide relocated by `#primeBoundary()` back to its logical DOM position, if any.
   * Safe to call unconditionally. Must run before any other navigation path (select(), pointer
   * drag) reads `this.slides`/`current`, since those assume DOM order matches logical order.
   * Never touches scroll position: priming only ever relocates an off-screen, non-current slide,
   * so undoing it has no visible effect on the current scroll position.
   */
  #restoreBoundaryPrime() {
    const primed = this.#boundaryPrime;
    if (!primed) return;

    this.#boundaryPrime = null;
    primed.relocation.restore();
  }

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    // Multi-visible-slide layouts (e.g. thumbnail rails showing several slides at once) don't
    // have a single well-defined "current" slide by live-geometry alone the way a one-slide-at-a-
    // time carousel does, so keep using the cached IntersectionObserver snapshot there - that
    // path isn't part of the boundary-priming race this method's single-slide branch used to hit
    // (see #closestSlide()/#landedOnPrimedSlide() for why single-slide sync no longer uses it).
    const visibleSlides = this.visibleSlides;

    if (visibleSlides.length > 1) {
      const { axis } = this.#scroll;
      const { scroller } = this.refs;
      const centers = visibleSlides.map((slide) => center(slide, axis));
      const referencePoint = scroller.getBoundingClientRect()[axis];
      const closestCenter = closest(centers, referencePoint);
      const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

      if (!closestVisibleSlide) return (this.current = 0);

      return (this.current = slides.indexOf(closestVisibleSlide));
    }

    const closestSlide = this.#closestSlide();
    if (!closestSlide) return this.current;

    return (this.current = slides.indexOf(closestSlide));
  };

  /**
   * Synchronously determines which logical slide the scroller is currently centered/settled on,
   * by measuring live geometry (`getBoundingClientRect`) over ALL logical slides rather than the
   * IntersectionObserver's asynchronously-cached `visibleSlides`. Used at points immediately
   * downstream of a synchronous DOM move (boundary priming/landing), where the cache is not
   * guaranteed to have caught up yet - see `#landedOnPrimedSlide()` and `#sync()`.
   * @returns {HTMLElement | undefined}
   */
  #closestSlide() {
    const { slides } = this;
    if (!slides?.length || !this.#scroll) return undefined;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = slides.map((slide) => center(slide, axis));
    const referencePoint = center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);

    return slides[centers.indexOf(closestCenter)];
  }

  /**
   * Clone-loop counterpart to `#closestSlide()`: measures live geometry over BOTH real
   * slides AND the two boundary clones (`#boundaryClones`), since a clone-loop scroll can
   * genuinely settle on a clone (that's the whole mechanism - see `#resolveCloneLanding()`).
   * `#closestSlide()` itself deliberately stays scoped to `this.slides` only (derived from
   * `refs.slides`, i.e. `ref="slides[]"` elements) because it's also used by the unrelated
   * non-clone-loop `#landedOnPrimedSlide()`/`#sync()` paths, which must never see a clone -
   * boundary clones intentionally carry no `ref` attribute for exactly that reason (see
   * `#buildBoundaryClone()`), so extending `#closestSlide()` itself risked changing behavior
   * those paths depend on. Only used from the clone-loop branch of `#handleScroll`.
   * @returns {HTMLElement | undefined}
   */
  #closestCloneLoopSlide() {
    const { slides } = this;
    if (!slides?.length || !this.#scroll) return undefined;

    const candidates = [...slides, ...this.#boundaryClones];

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = candidates.map((slide) => center(slide, axis));
    const referencePoint = center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);

    return candidates[centers.indexOf(closestCenter)];
  }

  /**
   * For a clone-loop slideshow, maps a DOM slide element (real or boundary clone) to its
   * logical index: a real slide's index in `this.slides`, or for a boundary clone, the
   * logical index it represents (the leading clone represents the last real slide's
   * index, the trailing clone represents index 0).
   * @param {HTMLElement} slide
   * @returns {number | undefined}
   */
  #logicalIndexForCloneLoopSlide(slide) {
    const { slides } = this;
    if (!slides?.length) return undefined;

    const realIndex = slides.indexOf(slide);
    if (realIndex !== -1) return realIndex;

    const [leadingClone, trailingClone] = this.#boundaryClones;
    if (slide === leadingClone) return slides.length - 1;
    if (slide === trailingClone) return 0;

    return undefined;
  }

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Refuse to start a NEW drag while a transition (select-triggered reorder, a previous drag's
    // wrap-reorder, or a native-scroll boundary landing) is still actively moving DOM/scroll
    // state. Starting a second drag mid-transition would read/mutate the track concurrently with
    // that still-in-progress transition, corrupting which physical slide element ends up where.
    // The queued request this drag will produce on release (if it produces one at all) is instead
    // handled by `#enqueueOrRun()` once the active transition clears.
    if (this.#activeTransition) return;
    if (this.cloneLoop && this.#cloneLandingInFlight) return;

    // Undo any proactive boundary priming before reading slides below - this drag path assumes
    // DOM order matches logical order, which a pending priming would violate.
    this.#restoreBoundaryPrime();

    const { slides } = this;

    if (!slides || slides.length <= 1) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = (event) => {
      controller.abort();

      this.#dragging = false;

      /** @type {DragRequest} */
      const request = {
        kind: 'drag',
        velocity,
        distanceTravelled,
        pointerId: event.pointerId,
      };

      this.#enqueueOrRun(request);
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  /**
   * Marks a pointer as physically down on the scroller - see `#pointersDown`. Only wired up
   * for clone-loop slideshows (see `#setupSlideshow()`/`disconnectedCallback()`): the pointer-
   * lift confirmation it feeds is only ever consulted by `#maybeResolveCloneLanding()`.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    this.#pointersDown.add(event.pointerId);
  };

  /**
   * Marks a pointer as physically lifted from the scroller - see `#pointersDown`. Bound to
   * `pointerup`/`pointercancel`/`pointercapturelost`, the same exhaustive "the pointer is
   * definitely no longer down" event set `#handleMouseDown`'s own drag-release cleanup already
   * uses. Once ALL pointers are up (the set is empty - an incidental second touch point lifting
   * first must not read as "the gesture has ended" while the primary finger is still down), re-
   * check whether a clone landing is waiting on exactly this confirmation (see
   * `#maybeResolveCloneLanding()`) - the other half of the gate, "scroll has genuinely stopped
   * moving", is checked there via `Scroller#isScrolling`.
   * @param {PointerEvent} event
   */
  #handlePointerLift = (event) => {
    this.#pointersDown.delete(event.pointerId);
    this.#maybeResolveCloneLanding('pointer-lift');
  };

  /**
   * The gate for the redesigned clone-landing correction: resolves `#restingOnClone` (if any)
   * once BOTH of the following hold, not just one -
   *
   *   1. Every pointer has physically lifted (`#pointersDown.size === 0`) - the direct
   *      gesture-end signal from `#handlePointerDown`/`#handlePointerLift`, in place of the old
   *      design's sole reliance on scroll-event quiet-time. Tracked per-pointer-ID so an
   *      incidental second touch point can't satisfy this while the primary finger is still down.
   *   2. The scroll has genuinely stopped moving, not just paused between finger-movement ticks.
   *
   * Called from two places, distinguished by `source` because condition 2 must be checked
   * differently depending on which one is asking:
   *
   *   - `'settle'`, from `#onTransitionEnd` (`Scroller`'s `onScrollEnd`, which fires exactly once
   *     per genuine settle episode): being called at all already IS confirmation condition 2
   *     holds - `Scroller#isScrolling` cannot be used to double-check this here, because it's
   *     still synchronously `true` at the point `onScrollEnd` itself fires (only flipped to
   *     `false` immediately after - see scrolling.js). This covers "pointer already lifted while
   *     a clone-loop drag was still resolving, then the scroll itself finally settles".
   *   - `'pointer-lift'`, from `#handlePointerLift`: firing here means the pointer JUST came up,
   *     with no accompanying settle confirmation - native momentum/inertial scrolling commonly
   *     continues significantly after a touch ends, so the scroll can still be actively carrying
   *     the position across further clone/real-slide boundaries at this exact moment.
   *     `Scroller#isScrolling` is read synchronously here (safe: this call arrives from a real
   *     DOM `pointerup`/`pointercancel` event, entirely outside `Scroller`'s own callback stack,
   *     so the flag accurately reflects current state) - if a scroll is still in flight, defer
   *     and let the eventual real settle (`'settle'`, above) perform the resolution instead.
   *
   * Both call sites are safe to call redundantly/speculatively: if the gate isn't fully satisfied
   * yet, or there's nothing resting to resolve, this is a no-op.
   * @param {'settle' | 'pointer-lift'} source
   */
  #maybeResolveCloneLanding(source) {
    const resting = this.#restingOnClone;
    if (!resting || !this.#scroll || this.#cloneLandingInFlight) return;

    // Gate condition 1: every pointer must be physically up. This is the direct signal that
    // replaces the old design's sole reliance on scroll-event quiet-time.
    if (this.#pointersDown.size > 0) {
      return;
    }

    // Gate condition 2: the scroll itself must have genuinely stopped moving - see the method
    // doc for why this is only checked (via `Scroller#isScrolling`) for the 'pointer-lift'
    // source; the 'settle' source already IS this confirmation.
    if (source === 'pointer-lift' && this.#scroll.isScrolling) {
      return;
    }

    this.#restingOnClone = null;
    this.#resolveCloneLanding(resting.clone, resting.previousIndex);
  }

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // If the IntersectionObserver reports zero visible slides, then the slideshow has intersected, but no slides meet the visibility threshold.
    // Probably that the slideshow's host is mid-animation or zero-layout, like in an animating modal.
    // Don't stamp aria-hidden="true" on every slide. Set the current slide to the first visible slide.
    if (visibleSlides.length === 0) return 0;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

/**
 * @typedef {Object} SelectRequest
 * @property {'select'} kind
 * @property {number|string|{id: string}|'next'|'previous'} input
 * @property {Event} [event]
 * @property {{animate?: boolean}} [options]
 */

/**
 * @typedef {Object} DragRequest
 * @property {'drag'} kind
 * @property {number} velocity
 * @property {number} distanceTravelled
 * @property {number} pointerId
 */

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
